/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return */
/* TODO: Add proper typing for API responses to remove these eslint-disable comments */

import { isEqual, omitBy } from 'lodash-es'
import { useRouter } from 'nuxt/app'
import { defineStore } from 'pinia'
import { computed, onServerPrefetch, ref, shallowRef, watch } from 'vue'
import { State, interpret } from 'xstate-v4'
import { Hierarchy } from '~/apps/hierarchies/domain/types'
import { getHierarchyRepository } from '~/apps/hierarchies/inversify.config'
import { routeToFilters } from '~/apps/pu-links/infrastructure/routeToFilters'
import { getCampsitesWizardMachine } from '~/apps/search-wizard/campsites-search/machine/campsitesWizardMachine'
import { isSearchUrlRoute } from '~/apps/search/isSearchUrlComponent'
import { SearchFilters } from '~/apps/search/schemas'
import { logger } from '../utility/logger'

const isDev = process.env.NODE_ENV !== 'production'
function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

export const useSearchWizardStore = defineStore('searchWizardStore', () => {
  const dataLoadPromise = ref<Promise<void>>()
  const resolveDataLoad = ref<() => void>()

  function initializeDataLoadPromise() {
    dataLoadPromise.value = new Promise<void>((resolve) => {
      resolveDataLoad.value = resolve
    })
  }

  initializeDataLoadPromise()

  const requestID = generateUUID()
  const reqId = ref(requestID)
  const campsitesWizardMachine = getCampsitesWizardMachine(useRuntimeConfig())
  const router = useRouter()
  const nuxtApp = useNuxtApp()
  const isDesktop = nuxtApp.$isDesktop
  // if (isDev)
  //   console.log('useSearchWizardStore created', requestID, route.fullPath)
  const searchStore = useSearchStoreSetup(usePinia())
  const lang = (router.currentRoute.value.params.lang as string) || 'en-gb'
  const wizardFilters = ref({} as SearchFilters)
  const previousWizardFilters = ref({} as SearchFilters)
  const service = interpret(
    campsitesWizardMachine.withContext({
      ...campsitesWizardMachine.initialState.context,
      filters: wizardFilters.value,
    }),
  )
  const hierarchy = ref(null as Hierarchy | null)
  const isClearSearch = ref(true)
  const ssrState = useState('search-wizard-store-state', () => '')
  const initialState = ssrState.value
    ? JSON.parse(ssrState.value)
    : service.initialState
  let state = State.create(initialState as typeof service.initialState)
  // Use shallowRef for XState context - replaced wholesale on each transition
  // This avoids Vue deeply tracking all wizard state properties (reduces reactivity overhead)
  const context = shallowRef(state.context)
  // Use shallowRef for states array - replaced wholesale on each transition
  // Prevents Vue from deep-tracking array contents and triggering unnecessary
  // re-renders of components that access this store (e.g., FilterAndSortFacets)
  const states = shallowRef(state.toStrings())
  const isSearchWizardOpen = ref<boolean | null>(null)
  const openOnFocus = ref(false)
  const panelOpen = ref(false)
  const isUpdatingCounts = ref(false)

  service
    .onTransition((newState, event) => {
      // logger(
      //   'useSearchWizard.onTransition',
      //   requestID,
      //   router.currentRoute.value.fullPath,
      //   newState.toStrings(),
      //   event.type,
      // )
      if (event.type === 'CLEAR_SEARCH_PARAMS') {
        isClearSearch.value = true
      }
      if (process.server) {
        ssrState.value = JSON.stringify(newState)
      }
      state = newState
      // toStrings() always returns a new array, so compare by value
      // to avoid triggering dependents when state strings haven't changed
      // (e.g., SET_FILTERS doesn't change the state node)
      const newStates = newState.toStrings()
      if (
        newStates.length !== states.value.length ||
        newStates.some((s, i) => s !== states.value[i])
      ) {
        states.value = newStates
      }
      // Only update context/wizardFilters when context reference changed.
      // XState preserves context identity when no assign actions run
      // (e.g., FOCUS_LOCATION), avoiding unnecessary reactive cascades.
      if (context.value !== newState.context) {
        context.value = newState.context
        wizardFilters.value = newState.context.filters
      }
    })
    .start(state)

  if (process.server) {
    onServerPrefetch(async () => {
      // if (isDev) {
      //   console.log(
      //     'useSearchWizardStore.onServerPrefetch',
      //     requestID,
      //     router.currentRoute.value.fullPath,
      //   )
      // }
      const serverRoute = router.currentRoute.value
      // const serverRoute = route
      // you need to set the filters for routes like csd
      const serverFilters = routeToFilters(serverRoute)
      await setFilters(serverFilters)
      // if (isDev) {
      //   console.log(
      //     'useSearchWizardStore.onServerPrefetch finished',
      //     requestID,
      //     router.currentRoute.value.fullPath,
      //   )
      // }
      // see if stopping the service cleans it up
      service.stop()
      resolveDataLoad.value?.()
      logger(
        'useSearchWizardStore.onServerPrefetch finished',
        requestID,
        JSON.stringify(context.value),
      )
    })
  } else {
    resolveDataLoad.value?.()
  }

  async function waitForDataLoad() {
    await dataLoadPromise.value
  }

  if (process.client) {
    // when navigating to searchedit pages, set wizard open
    router.beforeEach((to, _from, next) => {
      if (to.name?.startsWith('searchedit')) {
        isSearchWizardOpen.value = true
      }
      openOnFocus.value = Boolean(to.name === 'index')
      next()
    })

    // when a new route is done, use that for the wizard filters
    watch(
      () => routeToFilters(router.currentRoute.value),
      async (filters) => {
        // if (isDev) console.log('useSearchWizardStore.filtersChanged', filters)
        await setFilters(filters)
      },
    )

    // Add a watch effect for panelOpen to trigger updateCounts when the panel is opened
    watch(panelOpen, async (newVal) => {
      if (newVal === true) {
        setPreviousFilters(wizardFilters.value)
        if (isDev) {
          // eslint-disable-next-line no-console -- Debug logging for development
          console.log('useSearchWizardStore.panelOpen', newVal)
        }
        await updateCounts()
      }
    })
  }

  async function fetchHierarchyData(newHierarchyPath: string) {
    if (newHierarchyPath) {
      if (hierarchy.value?.path !== newHierarchyPath) {
        const repository = getHierarchyRepository()
        const hierarchyData = await repository.readOneByPath(
          newHierarchyPath,
          lang,
        )
        if (!hierarchyData || !Object.keys(hierarchyData).length) {
          hierarchy.value = null
          return
        }
        hierarchy.value = hierarchyData
      }
      service.send('SET_LOCATION_HIERARCHY', {
        data: {
          dataType: 'hierarchy',
          ...hierarchy.value,
        },
      })
    } else {
      hierarchy.value = null
    }
  }

  async function setFilters(filters: SearchFilters) {
    if (filters && !isEqual(wizardFilters.value, filters)) {
      wizardFilters.value = filters
      service.send({
        type: 'SET_FILTERS',
        filters,
      })
      // do not fetch campsite data
      if (
        filters.hierarchyPath &&
        !filters.hierarchyPath.startsWith('_') &&
        (!context?.value?.selectedLocation ||
          context?.value?.selectedLocation?.dataType === 'hierarchy')
      ) {
        await fetchHierarchyData(filters.hierarchyPath)
      } else {
        hierarchy.value = null
      }
      return true
    }
    return false
  }

  async function setWizardFilters(filters: SearchFilters) {
    if (await setFilters(filters)) {
      if (process.client) {
        await nextTick()
      }
      await updateCounts()
      return true
    }
    return false
  }

  function setPreviousFilters(filters) {
    previousWizardFilters.value = JSON.parse(JSON.stringify(filters))
  }

  async function resetToPreviousFilters() {
    await setFilters(previousWizardFilters.value)
  }

  async function setCategoryIds(categoryIds: string[]) {
    const newFilters = { ...wizardFilters.value, categoryIds }
    await setWizardFilters(newFilters)
  }

  // function to enable and disable facets in one go
  async function enableDisableFacets(enabled: string[], disabled: string[]) {
    const existingFacets = wizardFilters.value.facets ?? []
    const newFacets = Array.from(
      new Set([
        ...existingFacets.filter((facet) => !disabled.includes(facet)),
        ...enabled,
      ]),
    ).sort()
    logger('enableDisableFacets', enabled, disabled, newFacets)
    const newFilters = { ...wizardFilters.value, facets: newFacets }
    await setWizardFilters(newFilters)
  }

  const inputPlaceholder = computed(() => {
    const defaultPlaceholder = 'Where are you going?'
    const searchType = context?.value?.searchInputValue?.type
    const selectedLocation = context?.value?.selectedLocation

    // don't show on homepage unless wizard open (wizard always open on desktop)
    if (
      !isSearchWizardOpen.value &&
      !isSearchUrlRoute(router.currentRoute.value) &&
      !isDesktop
    ) {
      return defaultPlaceholder
    }
    if (myLocationPoint.value) {
      return 'Around current location...'
    }
    if (searchType === 'BOUNDS') {
      return 'Custom map area'
    }
    if (searchType === 'CUSTOM') {
      return context?.value?.searchInputValue?.text
    }
    if (selectedLocation?.name) {
      return selectedLocation.name
    }
    return defaultPlaceholder
  })

  const locationInputHasValue = computed(() => {
    return Boolean(
      context?.value?.searchInputValue?.type === 'LOCATION' &&
        (myLocationPoint.value || context?.value?.selectedLocation?.name),
    )
  })

  const totalCounts = computed(() => {
    return (
      searchStore.searchCounts?.bookableCount || searchStore.searchCounts?.count
    )
  })

  const counts = computed(() => {
    const { campsites: _campsites, ...otherCounts } =
      searchStore.searchCounts ?? {}
    return otherCounts
  })

  const myLocationPoint = computed(() => {
    if (
      context.value.filters.area?.lat &&
      context.value.filters.area?.lng &&
      !context.value.filters.placename
    ) {
      return context.value.filters.area
    }
    return undefined
  })

  const isHierarchyPoint = computed(() => {
    const selectedLocation = context?.value?.selectedLocation
    return Boolean(
      selectedLocation &&
        selectedLocation.dataType === 'hierarchy' &&
        selectedLocation.type === 3,
    )
  })

  const selectedHierarchyName = computed(() => {
    const selectedLocation = context?.value?.selectedLocation
    return selectedLocation?.name || ''
  })

  const shouldShowWithinInput = computed(() => {
    const point = context.value?.filters.area
    return isHierarchyPoint.value || (Boolean(point) && !point?.isEmptyPoint)
  })

  const showCount = computed(() => {
    const selectedLocation = context?.value?.selectedLocation
    const isCampsite = selectedLocation?.dataType === 'campsite'
    return Boolean(!isCampsite && totalCounts.value)
  })

  async function updateCounts() {
    // Defensive check: ensure store is initialized
    if (!service || !context.value) {
      console.error('updateCounts called before store initialization')
      return false
    }

    isUpdatingCounts.value = true
    try {
      if (process.client) {
        await nextTick()
      }
      const selectedLocation = context?.value?.selectedLocation
      const isCampsite = selectedLocation?.dataType === 'campsite'
      // only update counts if we are showing counts
      if (!isCampsite) {
        return await searchStore.countsSearch(wizardFilters.value)
      }
      return await Promise.resolve(false)
    } catch (error) {
      console.error('Error updating counts:', error)
      return false
    } finally {
      isUpdatingCounts.value = false
    }
  }

  const within = computed(() => {
    // use the within from the filters or the backend within
    return context.value.filters.within || searchStore.localizedWithin
  })

  // if our filters are different to current route filters
  const isSearchEnabled = computed(() => {
    const routeFilters = routeToFilters(router.currentRoute.value)
    const filters = wizardFilters.value
    // Remove undefined values from both filter objects
    const cleanedRouteFilters = omitBy(
      routeFilters,
      (value) => value === undefined,
    )
    const cleanedFilters = omitBy(filters, (value) => value === undefined)
    return !isEqual(cleanedRouteFilters, cleanedFilters)
  })

  // Create a safe send wrapper that checks if service is still active
  function safeSend(event: any, payload?: any) {
    try {
      // Check if service is initialized and not stopped
      if (!service || !service.send) {
        console.error(
          'Attempted to send event to stopped or uninitialized service:',
          event,
        )
        return
      }

      // Check if service is in a stopped state
      if ((service as any)._stopped || (service as any).status === 2) {
        console.error('Attempted to send event to stopped service:', event)
        return
      }

      if (payload) {
        service.send(event, payload)
      } else {
        service.send(event)
      }
    } catch (error) {
      console.error(
        'Error sending event to search wizard service:',
        event,
        error,
      )
    }
  }

  return {
    context,
    hierarchy,
    send: safeSend,
    service: service,
    states,
    inputPlaceholder,
    locationInputHasValue,
    isSearchEnabled,
    isClearSearch,
    wizardFilters,
    setWizardFilters,
    setFilters,
    ssrState,
    totalCounts,
    updateCounts,
    counts,
    myLocationPoint,
    shouldShowWithinInput,
    showCount,
    isSearchWizardOpen,
    openOnFocus,
    selectedHierarchyName,
    reqId,
    panelOpen,
    within,
    previousWizardFilters,
    setPreviousFilters,
    resetToPreviousFilters,
    waitForDataLoad,
    isUpdatingCounts,
    setCategoryIds,
    enableDisableFacets,
  }
})
